/**
 * Split mixed message attachments so the transcript can lay documents,
 * stills, and audio in separate rows instead of one wrapping jumble.
 */
import { isMediaOutputFile } from '@/constants/utils/message';

const GENERATED_SOURCES = new Set(['inference', 'ai_generated', 'ai-generated', 'coworker', 'work']);

function fileBaseName(file) {
  if (!file || typeof file !== 'object') return '';
  const raw = file.originalName || file.name || file.path || file.cdnPath || file.url || '';
  return String(raw).split(/[/\\]/).pop().split('?')[0].trim().toLowerCase();
}

function looksGeneratedFile(file) {
  const source = String(file?.source || '').toLowerCase();
  if (source === 'upload') return false;
  if (GENERATED_SOURCES.has(source)) return true;
  if (file?.path) return true;
  const size = file?.sizeInBytes || file?.size || 0;
  return !size;
}

function fileIdentityKeys(file) {
  if (!file || typeof file !== 'object') return [];
  const keys = [];
  const id = file.id || file._id;
  if (id) keys.push(`id:${id}`);
  const url = String(file.cdnPath || file.url || '').split('?')[0];
  if (url) {
    keys.push(`cdn:${url}`);
    keys.push(`url:${url.toLowerCase()}`);
  }
  if (file.fingerprint) keys.push(`fp:${file.fingerprint}`);
  const name = fileBaseName(file);
  const size = file.sizeInBytes || file.size || 0;
  if (name) {
    keys.push(`name:${name}|${size}`);
    if (looksGeneratedFile(file)) keys.push(`name:${name}`);
  }
  return keys;
}

function imageIdentityKeys(image) {
  if (!image) return [];
  if (typeof image === 'string') {
    const url = image.split('?')[0].toLowerCase();
    return url ? [`url:${url}`] : [];
  }
  const keys = fileIdentityKeys({ ...image, source: image.source || 'inference' });
  const url = String(image.url || image.cdnPath || image.src || '').split('?')[0].toLowerCase();
  if (url) keys.push(`url:${url}`);
  return keys;
}

/**
 * Assistant stills often land on both `message.images` and `message.files`.
 * Keep the file card and hide the extra image thumbnail.
 */
export function imagesNotCoveredByFiles(images, files) {
  if (!Array.isArray(images) || !images.length) return [];
  const list = Array.isArray(files) ? files : [];
  if (!list.length) return images;
  const seen = new Set();
  for (const file of list) {
    for (const key of fileIdentityKeys(file)) seen.add(key);
  }
  return images.filter((image) => {
    const keys = imageIdentityKeys(image);
    if (!keys.length) return true;
    return !keys.some((key) => seen.has(key));
  });
}

/**
 * Work deliverables get uploaded twice (live export + settle) and land as
 * two File docs with different ids but the same name/size/bytes. Collapse
 * those so the transcript does not render two identical video/image cards.
 */
export function dedupeMessageFiles(files) {
  const seen = new Set();
  const out = [];
  for (const file of Array.isArray(files) ? files : []) {
    if (!file || typeof file !== 'object' || file.isSuperseded) continue;
    const keys = fileIdentityKeys(file);
    if (keys.length && keys.some((key) => seen.has(key))) continue;
    keys.forEach((key) => seen.add(key));
    out.push(file);
  }
  return out;
}

export function groupMessageFiles(files) {
  const documents = [];
  const visuals = [];
  const audio = [];
  for (const file of dedupeMessageFiles(files)) {
    if (!file || typeof file !== 'object') continue;
    if (isMediaOutputFile(file, 'audio')) {
      audio.push(file);
    } else if (
      isMediaOutputFile(file, 'image')
      || isMediaOutputFile(file, 'video')
      || isMediaOutputFile(file, '3d')
    ) {
      visuals.push(file);
    } else {
      documents.push(file);
    }
  }
  return { documents, visuals, audio };
}
